import type { ReactNode } from 'react';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useMapStore } from '../stores/mapStore';
import { Icon, type IconName } from './Icon';

/**
 * MapControlsToolbar
 *
 * Single bottom-right control cluster shared by MapCanvas + IsoMapCanvas.
 * Two stacked rows inside one pill:
 *   Row 1 — zoom (+/%/fit/-) plus any view-specific `extras` (iso torch, help)
 *   Row 2 — view mode (Campaign / Top-Down / Iso)
 *
 * Styling mirrors the left-side VerticalToolbar (44×44 buttons, surface bg,
 * gold-accent when active) so both clusters feel like one system.
 */

interface ModeDef {
  id: 'campaign' | 'battle' | 'iso';
  label: string;
  icon: IconName;
}

// Three explicit views. Each button = one specific mode. No "smart"
// last-used routing — the top-down slot and iso slot are independent and
// the GM picks explicitly. Prevents the dual-highlight confusion and the
// "clicked Battle Map, landed somewhere unexpected" surprise.
const MODES: ModeDef[] = [
  { id: 'campaign', label: 'Campaign Map', icon: 'map' },
  { id: 'battle', label: 'Top-Down Battle Map', icon: 'sword' },
  { id: 'iso', label: 'Isometric Battle Map', icon: 'diamond' },
];

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.1;

function btnStyle(isActive: boolean): React.CSSProperties {
  return {
    width: 44,
    height: 44,
    borderRadius: 'var(--radius-md)',
    backgroundColor: isActive ? 'var(--color-primary)' : 'var(--color-background-elevated)',
    color: isActive ? 'var(--color-text)' : 'var(--color-text-secondary)',
    border: `var(--border-width-thin) solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all var(--transition-fast)',
    fontWeight: 600,
    fontSize: 11,
  };
}

// No hover handlers — mutating inline styles via e.currentTarget.style can
// leave stale backgrounds when the button's isActive flips mid-hover (was
// causing two buttons to both appear active on mode switch). Rely purely
// on the style prop from btnStyle(isActive).
function hoverHandlers(_isActive: boolean) {
  return {};
}

interface MapControlsToolbarProps {
  bottom: string;
  /** Fit-to-window handler — omit on views that don't support it (iso). */
  onFit?: () => void;
  /** Optional extra buttons rendered at the start of row 1 (e.g. iso torch/help). */
  extras?: ReactNode;
}

export function MapControlsToolbar({ bottom, onFit, extras }: MapControlsToolbarProps) {
  const mapMode = useCampaignMapStore((s) => s.mapMode);
  const setMapMode = useCampaignMapStore((s) => s.setMapMode);
  const zoom = useMapStore((s) => s.zoom);
  const setZoom = useMapStore((s) => s.setZoom);

  const zoomIn = () => setZoom(Math.min(MAX_ZOOM, zoom + ZOOM_STEP));
  const zoomOut = () => setZoom(Math.max(MIN_ZOOM, zoom - ZOOM_STEP));
  const zoomReset = () => setZoom(1);
  const isCustomZoom = Math.abs(zoom - 1) > 0.01;

  return (
    <div
      style={{
        position: 'absolute',
        bottom,
        right: 'var(--spacing-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-xs)',
        backgroundColor: 'var(--color-surface)',
        padding: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-lg)',
        border: 'var(--border-width-normal) solid var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 100,
        pointerEvents: 'auto',
      }}
    >
      {/* Row 1 — zoom + extras */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-xs)' }}>
        {extras}
        <button onClick={zoomIn} title="Zoom In" style={btnStyle(false)} {...hoverHandlers(false)}>
          <Icon name="plus" size={18} />
        </button>
        <button
          onClick={zoomReset}
          title={isCustomZoom ? 'Reset to 100%' : 'Zoom 100%'}
          style={{
            // Distinct amber style when zoom is off 100% — avoids looking
            // like an active MODE button (which uses primary pink).
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            backgroundColor: isCustomZoom ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-background-elevated)',
            color: isCustomZoom ? '#d4af37' : 'var(--color-text-secondary)',
            border: `var(--border-width-thin) solid ${isCustomZoom ? 'rgba(212, 175, 55, 0.5)' : 'var(--color-border)'}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all var(--transition-fast)',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {Math.round(zoom * 100)}%
        </button>
        {onFit && (
          <button onClick={onFit} title="Fit map to window" style={btnStyle(false)} {...hoverHandlers(false)}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>⊡</span>
          </button>
        )}
        <button onClick={zoomOut} title="Zoom Out" style={btnStyle(false)} {...hoverHandlers(false)}>
          <Icon name="minus" size={18} />
        </button>
      </div>

      {/* Row 2 — mode toggle (explicit, one button per view) */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-xs)' }}>
        {MODES.map((mode) => {
          const isActive = mapMode === mode.id;
          return (
            <button
              key={mode.id}
              onClick={() => setMapMode(mode.id)}
              title={mode.label}
              style={btnStyle(isActive)}
              {...hoverHandlers(isActive)}
            >
              <Icon name={mode.icon} size={20} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Legacy export preserved for any older imports. Prefer MapControlsToolbar.
export { MapControlsToolbar as MapModeToolbar };
